import { Config, type Method } from '@js-from-routes/client'
import { formatUrl as coreFormatUrl, type UrlOptions } from '@js-from-routes/core'

export interface PathHelper {
  (params?: UrlOptions): string
  pathTemplate: string
  httpMethod: Method
}

/**
 * Defines a path helper that can interpolate a URL path.
 *
 * @param {Method} method  An HTTP method
 * @param {string} pathTemplate The path with params placeholders (if any).
 */
function definePathHelper (method: Method, pathTemplate: string): PathHelper {
  const helper = (options?: UrlOptions) => formatUrl(pathTemplate, options)
  helper.pathTemplate = pathTemplate
  helper.httpMethod = method
  return helper
}

/**
 * Formats a url, replacing segments like /:id/ with the parameter of that name.
 * @param {string} urlTemplate A template URL with placeholders for params
 * @param {Query}  query       Query parameters to append to the URL
 * @param {Params} params      Parameters to interpolateUrl in the URL placeholders
 * @return {string} The interpolated URL with the provided query params (if any)
 * @example
 *   formatUrl('/users/:id', { id: '5' }) returns '/users/5'
 *   formatUrl('/users', { query: { id: '5' } }) returns '/users?id=5'
 */
function formatUrl (urlTemplate: string, options: UrlOptions = {}): string {
  let base = urlTemplate.startsWith('/') ? Config.baseUrl : ''
  if (base.endsWith('/')) base = base.slice(0, base.length - 1)
  return coreFormatUrl(`${base}${urlTemplate}`, options)
}

export {
  definePathHelper,
  formatUrl,
}
